import React from 'react';

/**
 * Renders the dynamic specification inputs for a category/sub-category
 * (schema from /api/attributes/resolve) and, read-only, the values a buyer chose.
 *
 *   <AttributeFields attributes={schema} values={vals} onChange={(k,v)=>…} />
 *   <AttributeValues items={demand.attributes} />
 */
export function AttributeFields({ attributes = [], values = {}, onChange, columns = 2 }) {
  if (!attributes.length) return null;

  const setVal = (key, v) => onChange && onChange(key, v);

  const toggleMulti = (key, opt) => {
    const cur = Array.isArray(values[key]) ? values[key] : [];
    setVal(key, cur.includes(opt) ? cur.filter(x => x !== opt) : [...cur, opt]);
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${columns > 1 ? 220 : 280}px, 1fr))`, gap: 14 }}>
      {attributes.map(a => (
        <div key={a.key} className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            {a.label}{a.unit ? ` (${a.unit})` : ''}
            {a.required && <span style={{ color: 'var(--red, #dc2626)' }}>*</span>}
          </label>

          {a.type === 'select' && (
            <select className="form-input" value={values[a.key] ?? ''} onChange={e => setVal(a.key, e.target.value)}>
              <option value="">Select {a.label.toLowerCase()}…</option>
              {a.options.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          )}

          {a.type === 'multiselect' && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {a.options.map(o => {
                const on = Array.isArray(values[a.key]) && values[a.key].includes(o);
                return (
                  <button
                    type="button" key={o} onClick={() => toggleMulti(a.key, o)}
                    style={{
                      padding: '6px 12px', borderRadius: 16, cursor: 'pointer', fontSize: 12.5, fontWeight: 600,
                      border: `1.5px solid ${on ? 'var(--green)' : 'var(--border)'}`,
                      background: on ? 'var(--green)' : '#fff', color: on ? '#fff' : 'var(--muted)',
                    }}
                  >{o}</button>
                );
              })}
            </div>
          )}

          {a.type === 'number' && (
            <input className="form-input" type="number" inputMode="decimal" value={values[a.key] ?? ''}
              placeholder={a.unit ? `e.g. 6 ${a.unit}` : 'Enter a number'}
              onChange={e => setVal(a.key, e.target.value)} />
          )}

          {a.type === 'text' && (
            <input className="form-input" value={values[a.key] ?? ''} placeholder={`Enter ${a.label.toLowerCase()}`}
              onChange={e => setVal(a.key, e.target.value)} />
          )}

          {a.type === 'boolean' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, color: 'var(--char, #1a1a1a)', cursor: 'pointer', paddingTop: 6 }}>
              <input type="checkbox" checked={!!values[a.key]} onChange={e => setVal(a.key, e.target.checked)} />
              Yes
            </label>
          )}
        </div>
      ))}
    </div>
  );
}

/** Read-only chips of the specs a buyer chose — used on lead/review screens. */
export function AttributeValues({ items = [], compact = false }) {
  if (!items || !items.length) return null;
  const fmt = (v) => Array.isArray(v) ? v.join(', ') : (typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v));
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: compact ? 5 : 7 }}>
      {items.map(a => {
        const displayLabel = /processor\/chipset/i.test(a.label) ? "Processor Type" : a.label;
        return (
          <span key={a.key} style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: compact ? '3px 9px' : '5px 11px', borderRadius: 8,
            background: 'rgba(27,92,58,0.07)', border: '1px solid rgba(27,92,58,0.18)',
            fontSize: compact ? 11.5 : 12.5, fontWeight: 600, color: 'var(--char, #1a1a1a)',
          }}>
            <span style={{ color: 'var(--muted)', fontWeight: 600 }}>{displayLabel}:</span>
            {fmt(a.value)}{a.unit ? ` ${a.unit}` : ''}
          </span>
        );
      })}
    </div>
  );
}

/** values object -> API payload [{key,value}] (skips blanks) */
export function toAttributePayload(attributes, values) {
  return (attributes || [])
    .map(a => ({ key: a.key, value: values[a.key] }))
    .filter(x => x.value !== undefined && x.value !== '' && !(Array.isArray(x.value) && !x.value.length));
}

/** item.specs (free-form {k:v}) -> prefill values for the schema, matching by key/label */
export function prefillFromSpecs(attributes, specs) {
  const out = {};
  if (!specs || typeof specs !== 'object') return out;
  const norm = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, '');

  const SYNONYMS = {
    chipset: 'processor',
    cpu: 'processor',
    soc: 'processor',
    processortype: 'processor',
    processorchipset: 'processor',
    screen: 'display',
    screentype: 'display',
    displaysize: 'display',
    color: 'colour',
    operatingsystem: 'os',
  };
  const getCanonical = (s) => {
    const n = norm(s);
    return SYNONYMS[n] || n;
  };

  const specByNorm = {};
  Object.entries(specs).forEach(([k, v]) => { specByNorm[getCanonical(k)] = v; });
  (attributes || []).forEach(a => {
    const hit = specByNorm[getCanonical(a.key)] ?? specByNorm[getCanonical(a.label)];
    if (hit === undefined || hit === null || hit === '') return;
    if (a.type === 'multiselect') {
      const arr = Array.isArray(hit) ? hit.map(String) : String(hit).split(/[,/]/).map(s => s.trim());
      const keep = arr.filter(x => a.options.includes(x));
      if (keep.length) out[a.key] = keep;
    } else if (a.type === 'select') {
      const val = String(hit).trim();
      const match = a.options.find(o => norm(o) === norm(val));
      if (match) out[a.key] = match;
    } else if (a.type === 'number') {
      const n = parseFloat(String(hit).replace(/[^\d.]/g, ''));
      if (isFinite(n)) out[a.key] = n;
    } else if (a.type === 'boolean') {
      out[a.key] = !!hit && String(hit).toLowerCase() !== 'false' && String(hit).toLowerCase() !== 'no';
    } else {
      out[a.key] = String(hit);
    }
  });
  return out;
}

export default AttributeFields;
